import React, { FC } from 'react';

import { Avatar, Spin, Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { SharedUserToDisplay } from '@/stores';
import styles from './PermissionDrawer.module.scss';

const MAX_VISIBLE_AVATARS = 3;
const DEFAULT_AVATAR_SIZE = 32;
const SPINNER_STYLE = { fontSize: 18 };
const TOOLTIP_OVERLAY_STYLE: React.CSSProperties = { pointerEvents: 'none' };

interface WhoHasAccessProps {
    users: SharedUserToDisplay[];
    loading?: boolean;
    onClick?: () => void;
}

export const WhoHasAccessAvatars: FC<{
    users: SharedUserToDisplay[];
    loading?: boolean;
    avatarSize?: number;
}> = ({ users, loading, avatarSize = DEFAULT_AVATAR_SIZE }) => {
    const activeUsers = users.filter((u) => !u.deleted);

    return (
        <div className={styles['who-has-access-avatars']} data-testid="permissionDrawer_whoHasAccessAvatars">
            {loading ? (
                <Spin indicator={<LoadingOutlined style={SPINNER_STYLE} />} />
            ) : (
                <>
                    {activeUsers.slice(0, MAX_VISIBLE_AVATARS).map((user) => (
                        <Tooltip
                            key={user.email}
                            title={user.email}
                            placement="bottom"
                            color="#091F56"
                            overlayStyle={TOOLTIP_OVERLAY_STYLE}
                        >
                            <Avatar className={styles['who-has-access-avatar']} size={avatarSize}>
                                {user.email[0]?.toUpperCase()}
                            </Avatar>
                        </Tooltip>
                    ))}
                    {activeUsers.length > MAX_VISIBLE_AVATARS && (
                        <Tooltip
                            placement="bottom"
                            color="#091F56"
                            overlayStyle={TOOLTIP_OVERLAY_STYLE}
                            title={(
                                <div>
                                    {activeUsers.slice(MAX_VISIBLE_AVATARS).map((u) => (
                                        <div key={u.email}>{u.email}</div>
                                    ))}
                                </div>
                            )}
                        >
                            <Avatar className={styles['who-has-access-overflow']} size={avatarSize}>
                                {`+${activeUsers.length - MAX_VISIBLE_AVATARS}`}
                            </Avatar>
                        </Tooltip>
                    )}
                </>
            )}
        </div>
    );
};

const WhoHasAccess: FC<WhoHasAccessProps> = ({ users, loading, onClick }) => {
    const { t } = useTranslation();
    const activeUsers = users.filter((u) => !u.deleted);
    const isClickable = !loading && activeUsers.length > 0;

    if (!loading && activeUsers.length === 0) {
        return (
            <span className={styles['who-has-access-empty']} data-testid="permissionDrawer_whoHasAccessEmpty">
                {t('filesTable.permissionDrawer.noExistingRecipients')}
            </span>
        );
    }

    return (
        <div
            className={styles['who-has-access']}
            data-testid="permissionDrawer_whoHasAccess"
            onClick={isClickable ? onClick : undefined}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' && isClickable) onClick?.(); }}
        >
            <span className={styles['who-has-access-label']}>
                {t('filesTable.permissionDrawer.whoHasAccess')}
            </span>
            <WhoHasAccessAvatars users={users} loading={loading} />
        </div>
    );
};

export default WhoHasAccess;
